import React from "react";
import { Link } from "react-router-dom";
import PageTitle from "../../components/PageTitle";
import { axiosget } from "../../helpers/httprequest";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FiBarChart2 } from "react-icons/fi";

const Progression = () => {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        fetchProgression();
    }, []);

    const fetchProgression = async () => {
        try {
            setLoading(true);
            const req = await axiosget("ms_student_progression");
            if (req.data?.type === "error") {
                throw new Error(req.data.message);
            }
            setData(req.data || []);
        } catch (err) {
            setError(err.message || "Failed to load progression history");
        } finally {
            setLoading(false);
        }
    };

    const getStatusVariant = (status) => {
        if (!status) return "default";
        const lower = status.toLowerCase();
        if (lower.includes("pass") || lower.includes("promoted")) return "success";
        if (lower.includes("probation") || lower.includes("withdraw")) return "danger";
        return "info";
    };

    return (
        <div>
            <PageTitle
                title="Progression History"
                sub="History of your academic standing across sessions"
            />

            {loading ? (
                <div className="space-y-3">
                    <Skeleton className="h-12 w-full" />
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : error ? (
                <Alert variant="danger"><AlertDescription>{error}</AlertDescription></Alert>
            ) : data.length === 0 ? (
                <div className="text-center py-12 text-neutral-400">
                    <FiBarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-lg font-medium text-neutral-600">No progression history found</p>
                    <p className="text-sm">Your academic progression records will appear here.</p>
                </div>
            ) : (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-neutral-50 border-b border-neutral-200">
                                <tr>
                                    {["Session", "Level", "Semester", "GPA", "CGPA", "Status", "Action"].map((h) => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {data.map((item, index) => (
                                    <tr key={item._id || index} className="hover:bg-neutral-50 transition-colors">
                                        <td className="px-4 py-3.5 text-sm text-neutral-800 font-medium">{item.fromsession?.title || item.fromsession?.name || "N/A"}</td>
                                        <td className="px-4 py-3.5 text-sm text-neutral-700">{item.fromlevel}</td>
                                        <td className="px-4 py-3.5 text-sm text-neutral-700">{item.fromsemester === 1 ? "First" : "Second"}</td>
                                        <td className="px-4 py-3.5 text-sm font-semibold text-neutral-900">{item.gpa?.toFixed(2) || "N/A"}</td>
                                        <td className="px-4 py-3.5 text-sm font-semibold text-neutral-900">{item.cgpa?.toFixed(2) || "N/A"}</td>
                                        <td className="px-4 py-3.5">
                                            <Badge variant={getStatusVariant(item.status)} size="sm" dot>{item.status || "Unknown"}</Badge>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <Link to={`/app/result?level=${item.fromlevel}&semester=${item.fromsemester}`}>
                                                <Button variant="outline" size="sm">View Result</Button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default Progression;
